import { createContext, useCallback, useContext } from 'react';
import axios from 'axios';

import { useAuth } from '@xemplo/auth-provider';
import { CustomAxios, CustomAxiosOptions } from '@xemplo/custom-axios';
import { XemploAppSettings } from '@xemplo/types';
import { AppSettingsContext } from '@xemplo/xemplo-app-settings';

// Consumer specs often mock @xemplo/xemplo-app-settings wholesale, leaving
// AppSettingsContext undefined — fall back to an empty context so this hook
// keeps working there (and resolves no hrInstanceUrl).
const FallbackAppSettingsContext = createContext<XemploAppSettings | undefined>(
  undefined,
);

const defaultTimeout = 30000;
const defaultProps = {
  timeout: defaultTimeout,
  withCredentials: false,
} as CustomAxiosOptions;

export const useHttpClient = ({
  timeout = defaultTimeout,
  withCredentials = false,
  ...rest
} = defaultProps) => {
  const { user, signinSilent, removeUser } = useAuth();
  // Read the context directly: useAppSettings throws outside the provider, and
  // this hook must keep working in tests and non-app contexts without one.
  const hrInstanceUrl = useContext(
    AppSettingsContext ?? FallbackAppSettingsContext,
  )?.hrInstanceUrl;

  const onUnauthorized = useCallback(async () => {
    const renewedUser = await signinSilent();
    return renewedUser?.access_token;
  }, [signinSilent]);

  const onAuthFailure = useCallback(async () => {
    await removeUser();
  }, [removeUser]);

  const client = new CustomAxios({
    timeout,
    withCredentials,
    ...rest,
    authToken: user?.access_token,
    // The HR instance resolves the current persona from the xemploSessionId
    // session cookie, so its requests must carry credentials.
    originsWithCredentials: hrInstanceUrl ? [hrInstanceUrl] : undefined,
    onUnauthorized,
    onAuthFailure,
  }).client();

  return { client, cancelSignal: axios.CancelToken.source(), user };
};
